/**
 * Hook for managing push notification subscription state.
 *
 * Shared between SettingsDialog and PushNotificationManager.
 */

import { useEffect, useCallback, useState } from 'react';
import { useApiBase } from './useApiBase';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../lib/authFetch';
import { getCurrentDeviceKind } from '../lib/deviceClassification';

// VAPID public key - must match backend
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

export type PushNotificationState = {
  /** Whether push notifications are supported in this browser */
  isSupported: boolean;
  /** Whether the user is currently subscribed to push notifications */
  isSubscribed: boolean;
  /** Whether a subscribe/unsubscribe operation is in progress */
  isLoading: boolean;
  /** Current notification permission state */
  permissionState: NotificationPermission | null;
  /** Whether VAPID is configured */
  isConfigured: boolean;
  /** Last user-visible error, if any */
  error: string | null;
  /** Subscribe to push notifications */
  subscribe: () => Promise<boolean>;
  /** Unsubscribe from push notifications */
  unsubscribe: () => Promise<boolean>;
  /** Toggle subscription state */
  toggle: () => Promise<boolean>;
  /** Register service worker without subscribing */
  registerServiceWorker: () => Promise<ServiceWorkerRegistration | null>;
};

export function usePushNotifications(): PushNotificationState {
  const apiBase = useApiBase();
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionState, setPermissionState] = useState<NotificationPermission | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isConfigured = Boolean(VAPID_PUBLIC_KEY);

  // Check if push is supported
  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    setIsSupported(supported);

    if (supported) {
      setPermissionState(Notification.permission);
    }
  }, []);

  // Check existing subscription on mount
  useEffect(() => {
    if (!isSupported) return;

    async function checkSubscription() {
      try {
        const registration = await navigator.serviceWorker.getRegistration('/mobile-sw.js');
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        }
      } catch (err) {
        setError('Unable to check push notification status on this device.');
      }
    }

    checkSubscription();
  }, [isSupported]);

  // Register service worker
  const registerServiceWorker = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (!isSupported) return null;

    try {
      const registration = await navigator.serviceWorker.register('/mobile-sw.js', {
        scope: '/',
      });
      if (import.meta.env.DEV) {
        console.log('[Push] Service worker registered:', registration.scope);
      }
      return registration;
    } catch (err) {
      setError('Failed to initialize push notifications on this device.');
      return null;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !user || !isConfigured) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const deviceKind = getCurrentDeviceKind();

      // Register service worker if not already
      let registration: ServiceWorkerRegistration | null | undefined = await navigator.serviceWorker.getRegistration('/mobile-sw.js');
      if (!registration) {
        registration = await registerServiceWorker();
      }

      if (!registration) {
        throw new Error('Service worker registration failed');
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      if (permission !== 'granted') {
        setError('Notifications are blocked for this site in your browser settings.');
        setIsLoading(false);
        return false;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Send subscription to backend
      const response = await authFetch(`${apiBase}/api/push-subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth')),
          },
          platform: deviceKind === 'phone' ? 'mobile' : deviceKind,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to register subscription');
      }

      setIsSubscribed(true);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable push notifications on this device.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user, isConfigured, apiBase, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.getRegistration('/mobile-sw.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          // Unsubscribe locally
          await subscription.unsubscribe();

          // Remove from backend
          await authFetch(`${apiBase}/api/push-subscriptions`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ endpoint: subscription.endpoint }),
          });
        }
      }

      setIsSubscribed(false);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable push notifications on this device.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiBase]);

  // Toggle subscription
  const toggle = useCallback(async (): Promise<boolean> => {
    if (isSubscribed) {
      return unsubscribe();
    } else {
      return subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  // Auto-register service worker on mount (but don't auto-subscribe)
  useEffect(() => {
    if (isSupported && user && isConfigured) {
      registerServiceWorker();
    }
  }, [isSupported, user, isConfigured, registerServiceWorker]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permissionState,
    isConfigured,
    error,
    subscribe,
    unsubscribe,
    toggle,
    registerServiceWorker,
  };
}

// Helper: Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper: Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default usePushNotifications;
